import { Scene, GameObjects } from 'phaser';

export interface MenuButtonConfig {
  scene: Scene;
  x: number;
  y: number;
  text: string;
  width?: number;
  height?: number;
  fontSize?: number;
  onClick?: () => void;
  disabled?: boolean;
}

export class MenuButton extends GameObjects.Container {
  private bg: GameObjects.Rectangle;
  private label: GameObjects.Text;
  private isDisabled: boolean;
  private onClick: (() => void) | undefined;
  private isPressed: boolean = false;

  // Colors
  private static readonly COLOR_NORMAL = 0x333333;
  private static readonly COLOR_HOVER = 0x555555;
  private static readonly COLOR_DOWN = 0x222222;
  private static readonly COLOR_DISABLED = 0x222222;
  private static readonly COLOR_BORDER = 0x00ff00;
  private static readonly COLOR_BORDER_DISABLED = 0x444444;

  constructor(config: MenuButtonConfig) {
    super(config.scene, config.x, config.y);

    const width = config.width ?? 280;
    const height = config.height ?? 60;
    const fontSize = config.fontSize ?? 28;

    this.isDisabled = config.disabled ?? false;
    this.onClick = config.onClick;

    // Background rectangle
    this.bg = config.scene.add.rectangle(0, 0, width, height, MenuButton.COLOR_NORMAL);
    this.bg.setStrokeStyle(3, this.isDisabled ? MenuButton.COLOR_BORDER_DISABLED : MenuButton.COLOR_BORDER);
    this.add(this.bg);

    // Text label
    this.label = config.scene.add.text(0, 0, config.text, {
      fontFamily: 'Arial Black',
      fontSize: `${fontSize}px`,
      color: this.isDisabled ? '#666666' : '#ffffff',
    }).setOrigin(0.5);
    this.add(this.label);

    // Make interactive
    this.setSize(width, height);
    this.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(-width * 0.5, -height * 0.5, width, height),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: !this.isDisabled,
    });

    if (!this.isDisabled) {
      this.on('pointerover', this.onHover, this);
      this.on('pointerout', this.onOut, this);
      this.on('pointerdown', this.onDown, this);
      this.on('pointerup', this.onUp, this);
      this.on('pointerupoutside', this.onUpOutside, this);
      this.on('pointermove', this.onMove, this);
    }

    // Add to scene
    config.scene.add.existing(this);
  }

  private onHover(): void {
    if (this.isPressed) return;
    this.bg.setFillStyle(MenuButton.COLOR_HOVER);
    this.label.setScale(1.05);
  }

  private onOut(): void {
    if (this.isPressed) return;
    this.bg.setFillStyle(MenuButton.COLOR_NORMAL);
    this.label.setScale(1);
  }

  private onDown(): void {
    this.isPressed = true;
    this.bg.setFillStyle(MenuButton.COLOR_DOWN);
    this.setScale(0.97);
  }

  private onUp(): void {
    if (!this.isPressed) return;
    this.isPressed = false;
    this.bg.setFillStyle(MenuButton.COLOR_NORMAL);
    this.label.setScale(1);
    this.setScale(1);
    if (this.onClick) {
      this.onClick();
    }
  }

  private onUpOutside(): void {
    this.isPressed = false;
    this.bg.setFillStyle(MenuButton.COLOR_NORMAL);
    this.label.setScale(1);
    this.setScale(1);
  }

  private onMove(pointer: Phaser.Input.Pointer): void {
    if (!this.isPressed) return;
    const bounds = new Phaser.Geom.Rectangle(-this.bg.width * 0.5, -this.bg.height * 0.5, this.bg.width, this.bg.height);
    const isInBounds = Phaser.Geom.Rectangle.Contains(bounds, pointer.x - this.x, pointer.y - this.y);
    if (!isInBounds && this.isPressed) {
      this.isPressed = false;
      this.bg.setFillStyle(MenuButton.COLOR_NORMAL);
      this.label.setScale(1);
      this.setScale(1);
    }
  }

  setDisabled(disabled: boolean): void {
    this.isDisabled = disabled;
    this.isPressed = false; // Reset pressed state
    this.bg.setFillStyle(disabled ? MenuButton.COLOR_DISABLED : MenuButton.COLOR_NORMAL);
    this.bg.setStrokeStyle(3, disabled ? MenuButton.COLOR_BORDER_DISABLED : MenuButton.COLOR_BORDER);
    this.label.setColor(disabled ? '#666666' : '#ffffff');
    this.label.setScale(1);
    this.setScale(1);
    
    if (disabled) {
      this.disableInteractive();
    } else {
      this.setInteractive({
        hitArea: new Phaser.Geom.Rectangle(-this.bg.width * 0.5, -this.bg.height * 0.5, this.bg.width, this.bg.height),
        hitAreaCallback: Phaser.Geom.Rectangle.Contains,
        useHandCursor: true,
      });
    }
  }

  setText(text: string): void {
    this.label.setText(text);
  }
}
